import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { useAppSelector, selectGroupGuid } from 'slices/commonSelectors';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';

interface RootState {
  app: {
    user: {
      user_guid: string;
    };
  };
}

interface RelationshipEntity {
  id: string;
  name: string;
  [key: string]: any;
}

/** Hook to fetch relationships for either a resource or member */
export const useResourceRelationships = (
  entityId: string,
  entityType: 'resource' | 'member',
  enabled = true
) => {
  const endpoint =
    entityType === 'resource'
      ? `${config.apiGateway.ics_resource_sets}/${entityId}/relationships`
      : `${config.apiGateway.ics_resource_sets}/member/${entityId}/relationships`;

  return useQuery<RelationshipEntity[]>({
    queryKey: ['resource-relationships', entityId, entityType],
    queryFn: async () => {
      const response = await axios.get(endpoint);
      return response.data;
    },
    enabled: enabled && !!entityId,
  });
};

/** Hook to update relationships for either a resource or member */
export const useUpdateResourceRelationships = () => {
  const queryClient = useQueryClient();
  const { user_guid } = useSelector((state: RootState) => state.app.user);
  const group_guid = useAppSelector(selectGroupGuid);
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      entityId,
      entityType,
      relatedIds,
    }: {
      entityId: string;
      entityType: 'resource' | 'member';
      relatedIds: string[];
    }) => {
      const endpoint =
        entityType === 'resource'
          ? `${config.apiGateway.ics_resource_sets}/${entityId}/relationships`
          : `${config.apiGateway.ics_resource_sets}/member/${entityId}/relationships`;

      const response = await axios.put(endpoint, {
        relatedIds,
        user_guid,
        group_guid,
        incident_id: incidentId ?? null,
      });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate both resource and member queries to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['resource-relationships'],
      });

      // Also invalidate resource sets and roster queries
      queryClient.invalidateQueries({
        queryKey: ['ICS-resource-sets'],
      });
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster'],
      });

      toast.success(
        variables.entityType === 'resource'
          ? 'Resource members updated successfully'
          : 'Member resources updated successfully'
      );
    },
    onError: (error: any) => {
      console.error('Error updating relationships:', error);
      toast.error(
        error.response?.data?.error || 'Failed to update relationships'
      );
    },
  });
};
